import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  FaSpinner, FaTimes, FaLock, FaSave,
  FaAlignLeft, FaCalendarAlt, FaTag, FaCar,
  FaListUl, FaPaperclip, FaRedo, FaSyncAlt,
  FaChevronDown, FaMoneyBillWave, FaCheck,
  FaLightbulb, FaFileInvoiceDollar,
} from 'react-icons/fa';
import AnexoViewerModal from './AnexoViewerModal';
import AnexosGaleria from './AnexosGaleria';
import './ModalContasAPagar.css';

const ModalContasAPagar = ({ isOpen, onClose, onSave, contaToEdit }) => {

  const isEditing = !!contaToEdit;

  // ── Form state ─────────────────────────────────────────────
  const [descricao,             setDescricao]             = useState('');
  const [valor,                 setValor]                 = useState('');
  const [dataVencimento,        setDataVencimento]        = useState('');
  const [categoriaId,           setCategoriaId]           = useState('');
  const [planoContasId,         setPlanoContasId]         = useState('');
  const [veiculoId,             setVeiculoId]             = useState('');
  const [anexo,                 setAnexo]                 = useState(null);
  const [isRecorrente,          setIsRecorrente]          = useState(false);
  const [frequencia,            setFrequencia]            = useState('mensal');
  const [parcelas,              setParcelas]              = useState(12);
  const [dataInicioRecorrencia, setDataInicioRecorrencia] = useState('');
  const [numeroNota,            setNumeroNota]            = useState(''); // [NF]

  // ── Auxiliary data ─────────────────────────────────────────
  const [categorias,   setCategorias]   = useState([]);
  const [veiculos,     setVeiculos]     = useState([]);
  const [planoContas,  setPlanoContas]  = useState([]);
  const [currentUser,  setCurrentUser]  = useState(null);
  const [isLoading,    setIsLoading]    = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingIA,    setLoadingIA]    = useState(false);

  // [ANEXOS] Visualização dos comprovantes já enviados (ex.: prestação via App)
  const [anexoParaVer, setAnexoParaVer] = useState(null);

  const fileInputRef = useRef(null);

  // ── Init ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) { resetForm(); return; }

    setIsLoading(true);
    try { setCurrentUser(JSON.parse(localStorage.getItem('user') || '{}')); } catch {}

    Promise.all([
      api.get('/api/gastos/categorias').catch(() => ({ data: [] })),
      api.get('/api/veiculos/select').catch(() => ({ data: [] })),
      api.get('/api/financeiro/plano-contas').catch(() => ({ data: [] })),
    ]).then(([catRes, vecRes, planoRes]) => {
      setCategorias(Array.isArray(catRes.data)    ? catRes.data   : []);
      setVeiculos(Array.isArray(vecRes.data)      ? vecRes.data   : []);
      setPlanoContas(Array.isArray(planoRes.data) ? planoRes.data : []);

      // Pre-fill when editing
      if (contaToEdit) {
        const fmtISO = (s) => s ? String(s).split('T')[0] : '';
        setDescricao(contaToEdit.descricao || '');
        setValor(contaToEdit.valor || '');
        setDataVencimento(fmtISO(contaToEdit.data_vencimento));
        setCategoriaId(contaToEdit.categoria_id || '');
        setPlanoContasId(contaToEdit.plano_contas_id || '');
        setVeiculoId(contaToEdit.veiculo_id || '');
        setNumeroNota(contaToEdit.numero_nota || ''); // [NF]
      }
    }).catch(() => toast.error('Falha ao carregar dados do formulário.'))
      .finally(() => setIsLoading(false));
  }, [isOpen, contaToEdit]);

  const resetForm = () => {
    setDescricao(''); setValor(''); setDataVencimento('');
    setCategoriaId(''); setPlanoContasId(''); setVeiculoId('');
    setAnexo(null); setIsRecorrente(false);
    setFrequencia('mensal'); setParcelas(12); setDataInicioRecorrencia('');
    setAnexoParaVer(null);
    setNumeroNota(''); // [NF]
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Category → Chart of accounts mapping ───────────────────
  const handleCategoriaChange = (e) => {
    const id = e.target.value;
    setCategoriaId(id);
    const cat = categorias.find(c => c.id === parseInt(id));
    if (cat?.plano_contas_id_padrao) setPlanoContasId(cat.plano_contas_id_padrao);
  };

  const handleSugerirConta = async () => {
    if (!descricao.trim() || !categoriaId) {
      toast.warn('Preencha a descrição e a categoria para usar a sugestão da IA.');
      return;
    }

    setLoadingIA(true);
    try {
      const catNome = categorias.find(c => c.id === parseInt(categoriaId))?.nome;

      const response = await api.post('/api/gastos/sugerir-conta-pagar', {
        descricao:      descricao,
        categoria_nome: catNome,
        valor:          valor || 0,
      });

      if (response.data?.id) {
        setPlanoContasId(response.data.id);
        toast.info(`Sugestão aplicada: ${response.data.nome}`);
      }
    } catch (e) {
      toast.error('Não foi possível obter uma sugestão no momento.');
    } finally {
      setLoadingIA(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setAnexo(file);
  };

  const removeFile = () => {
    setAnexo(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Submit ──────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!descricao.trim())                { toast.warn('Descrição é obrigatória.'); return; }
    if (!valor || parseFloat(valor) <= 0) { toast.warn('Informe um valor válido.'); return; }
    if (!dataVencimento)                  { toast.warn('Informe a data de vencimento.'); return; }

    setIsSubmitting(true);

    try {
      if (isEditing) {
        // ── UPDATE ──────────────────────────────────────────
        // FormData p/ carregar o anexo novo (multer lê 'anexo' no PUT).
        // Sem Content-Type manual: o axios injeta o boundary do multipart.
        const fd = new FormData();
        fd.append('descricao',       descricao.trim());
        fd.append('valor',           valor);
        fd.append('data_vencimento', dataVencimento);
        fd.append('categoria_id',    categoriaId    || '');
        fd.append('plano_contas_id', planoContasId  || '');
        fd.append('veiculo_id',      veiculoId      || '');
        if (numeroNota?.trim()) fd.append('numero_nota', numeroNota.trim()); // [NF]
        if (anexo) fd.append('anexo', anexo); // só sobe se escolheu arquivo novo
        await api.put(`/api/contas-a-pagar/${contaToEdit.id}`, fd);
        toast.success('Conta atualizada com sucesso!');
      } else {
        // ── CREATE ──────────────────────────────────────────
        const fd = new FormData();
        fd.append('descricao',       descricao.trim());
        fd.append('valor',           valor);
        fd.append('data_vencimento', dataVencimento);
        fd.append('categoria_id',    categoriaId);
        fd.append('plano_contas_id', planoContasId);
        fd.append('veiculo_id',      veiculoId);
        if (numeroNota?.trim()) fd.append('numero_nota', numeroNota.trim()); // [NF]
        if (anexo) fd.append('anexo', anexo);
        fd.append('isRecorrente', isRecorrente);
        if (isRecorrente) {
          fd.append('frequencia',              frequencia);
          fd.append('parcelas',                parcelas);
          fd.append('data_inicio_recorrencia', dataInicioRecorrencia || dataVencimento);
        }
        // NÃO fixar Content-Type: o axios injeta o boundary do multipart.
        // Com 'multipart/form-data' cravado à mão (sem boundary), o multer
        // não separa as partes → req.file undefined → anexo descartado.
        await api.post('/api/contas-a-pagar/manual', fd);
        toast.success(isRecorrente ? `${parcelas} parcela(s) criada(s) com sucesso!` : 'Conta registrada com sucesso!');
      }

      onSave();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao salvar conta.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canEditNatureza = ['admin_geral', 'admin'].includes(currentUser?.perfil);
  const FREQ_LABEL = { mensal: 'Mensal', semanal: 'Semanal', anual: 'Anual' };

  if (!isOpen) return null;

  return (
    <>
      <div className="mcp-overlay" onClick={onClose}>
        <div className="mcp-panel" onClick={e => e.stopPropagation()}>

          {/* ── Header ── */}
          <div className="mcp-header">
            <div className="mcp-header-text">
              <span className="mcp-eyebrow">Contas a Pagar</span>
              <h2 className="mcp-title">
                {isEditing ? 'Editar Conta' : isRecorrente ? 'Lançar Despesa Recorrente' : 'Nova Conta a Pagar'}
              </h2>
            </div>
            <button className="mcp-close" onClick={onClose} title="Fechar">
              <FaTimes />
            </button>
          </div>

          {/* ── Body ── */}
          {isLoading ? (
            <div className="mcp-loading">
              <FaSpinner className="mcp-spin" /> Carregando dados…
            </div>
          ) : (
            <form className="mcp-form" onSubmit={handleSubmit} noValidate>
              <div className="mcp-grid">

                {/* Descrição */}
                <div className="mcp-field span-2">
                  <label><FaAlignLeft /> Descrição <span className="req">*</span></label>
                  <input
                    type="text"
                    placeholder="Ex: Aluguel · Fornecedor X · Contrato Y"
                    value={descricao}
                    onChange={e => setDescricao(e.target.value)}
                    autoFocus required
                  />
                </div>

                {/* Valor */}
                <div className="mcp-field">
                  <label><FaMoneyBillWave /> Valor <span className="req">*</span></label>
                  <div className="input-currency">
                    <span className="currency-prefix">R$</span>
                    <input
                      type="number" step="0.01" min="0.01"
                      placeholder="0,00"
                      value={valor}
                      onChange={e => setValor(e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Vencimento */}
                <div className="mcp-field">
                  <label><FaCalendarAlt /> Vencimento <span className="req">*</span></label>
                  <input
                    type="date"
                    value={dataVencimento}
                    onChange={e => setDataVencimento(e.target.value)}
                    required
                  />
                </div>

                {/* Divider */}
                <div className="mcp-divider span-2"><span>Classificação</span></div>

                {/* Categoria */}
                <div className="mcp-field">
                  <label><FaTag /> Categoria Operacional</label>
                  <select value={categoriaId} onChange={handleCategoriaChange}>
                    <option value="">— Selecione —</option>
                    {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                </div>

                {/* Natureza contábil */}
                <div className="mcp-field">
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span><FaListUl /> Natureza Contábil {!canEditNatureza && <FaLock className="field-lock" />}</span>
                    <button
                      type="button"
                      className="mcp-ia-btn"
                      onClick={handleSugerirConta}
                      disabled={loadingIA || !canEditNatureza}
                      title="Sugerir com IA"
                    >
                      {loadingIA ? <FaSpinner className="btn-spin" /> : <FaLightbulb />}
                    </button>
                  </label>
                  <select
                    value={planoContasId}
                    onChange={e => setPlanoContasId(e.target.value)}
                    disabled={!canEditNatureza}
                    className={!canEditNatureza ? 'field-locked' : ''}
                  >
                    <option value="">— Enquadramento —</option>
                    {planoContas.map(c => (
                      <option key={c.id} value={c.id}>{c.codigo} · {c.nome}</option>
                    ))}
                  </select>
                </div>

                {/* Veículo */}
                <div className="mcp-field span-2">
                  <label><FaCar /> Vincular Veículo <span className="field-hint">(opcional · para Rentabilidade)</span></label>
                  <select value={veiculoId} onChange={e => setVeiculoId(e.target.value)}>
                    <option value="">— Nenhum —</option>
                    {veiculos.map(v => (
                      <option key={v.id} value={v.id}>{v.placa} · {v.modelo}</option>
                    ))}
                  </select>
                </div>

                {/* Divider Documentação */}
                <div className="mcp-divider span-2"><span>Documentação</span></div>

                {/* [NF] Nº da Nota Fiscal */}
                <div className="mcp-field span-2">
                  <label>
                    <FaFileInvoiceDollar /> Nº da Nota Fiscal
                    <span className="field-hint"> (opcional · NF-e, NFSe, Recibo)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: NF-e 000123 · NFSe 456 · Recibo 00789"
                    value={numeroNota}
                    onChange={e => setNumeroNota(e.target.value)}
                    maxLength={100}
                  />
                </div>

                {/* [ANEXOS] Comprovantes já enviados (prestação via App ou anexo do gasto) */}
                {isEditing && contaToEdit?.anexo_url && (
                  <div className="mcp-field span-2">
                    <label><FaPaperclip /> Comprovantes Enviados</label>
                    <div className="mcp-anexos-box">
                      <AnexosGaleria
                        anexoUrl={contaToEdit.anexo_url}
                        titulo="Recibos"
                        onOpen={(item) => setAnexoParaVer(item)}
                      />
                    </div>
                  </div>
                )}

                {/* Anexo (novo upload) */}
                <div className="mcp-field span-2">
                  <label>
                    <FaPaperclip /> {isEditing ? 'Anexar Novo Boleto / NF-e' : 'Anexar Boleto / NF-e'}
                  </label>
                  {anexo ? (
                    <div className="file-selected">
                      <FaPaperclip className="file-icon" />
                      <span className="file-name">{anexo.name}</span>
                      <span className="file-size">({(anexo.size / 1024).toFixed(1)} KB)</span>
                      <button type="button" className="file-remove" onClick={removeFile} title="Remover">
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <label className="file-upload-label" htmlFor="mcp-file-input">
                      <FaPaperclip className="upload-icon" />
                      <span>Clique para selecionar um arquivo</span>
                      <small>PDF, JPG, PNG — máx. 10 MB</small>
                    </label>
                  )}
                  <input
                    id="mcp-file-input"
                    type="file"
                    ref={fileInputRef}
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="file-input-hidden"
                  />
                </div>

              </div>{/* /mcp-grid */}

              {/* ── Recorrência (somente na criação) ── */}
              {!isEditing && (
              <div className="mcp-recorrencia">
                <button
                  type="button"
                  className={`recorr-toggle ${isRecorrente ? 'active' : ''}`}
                  onClick={() => setIsRecorrente(v => !v)}
                >
                  <span className="recorr-toggle-left">
                    <span className={`recorr-checkbox ${isRecorrente ? 'checked' : ''}`}>
                      {isRecorrente && <FaCheck />}
                    </span>
                    <FaSyncAlt className="recorr-icon" />
                    <span className="recorr-label">Despesa recorrente</span>
                    {isRecorrente && (
                      <span className="recorr-summary">
                        {parcelas}× {FREQ_LABEL[frequencia].toLowerCase()}
                      </span>
                    )}
                  </span>
                  <FaChevronDown className={`recorr-chevron ${isRecorrente ? 'open' : ''}`} />
                </button>

                {isRecorrente && (
                  <div className="recorr-fields">
                    <div className="mcp-field">
                      <label>Frequência</label>
                      <select value={frequencia} onChange={e => setFrequencia(e.target.value)}>
                        <option value="mensal">Mensal</option>
                        <option value="semanal">Semanal</option>
                        <option value="anual">Anual</option>
                      </select>
                    </div>
                    <div className="mcp-field">
                      <label>Nº de Parcelas</label>
                      <input
                        type="number" min="2" max="360"
                        value={parcelas}
                        onChange={e => setParcelas(Math.min(360, Math.max(2, parseInt(e.target.value) || 2)))}
                      />
                    </div>
                    <div className="mcp-field">
                      <label>Data da 1ª Parcela</label>
                      <input
                        type="date"
                        value={dataInicioRecorrencia}
                        onChange={e => setDataInicioRecorrencia(e.target.value)}
                        placeholder={dataVencimento}
                      />
                    </div>
                    <div className="recorr-preview">
                      <span className="recorr-preview-label">Serão criados</span>
                      <strong className="recorr-preview-val">{parcelas} lançamentos</strong>
                      <span className="recorr-preview-label">{FREQ_LABEL[frequencia].toLowerCase()}s</span>
                    </div>
                  </div>
                )}
              </div>
              )}{/* end !isEditing */}

              {/* ── Footer ── */}
              <div className="mcp-footer">
                {!isEditing && (
                  <button type="button" className="mcp-reset-btn" onClick={resetForm} disabled={isSubmitting}>
                    <FaRedo /> Limpar
                  </button>
                )}
                {isEditing && (
                  <button type="button" className="mcp-reset-btn" onClick={onClose} disabled={isSubmitting}>
                    <FaTimes /> Cancelar
                  </button>
                )}
                <button type="submit" className="mcp-save-btn" disabled={isSubmitting}>
                  {isSubmitting
                    ? <><FaSpinner className="mcp-spin" /> Salvando…</>
                    : isEditing
                      ? <><FaSave /> Salvar Alterações</>
                      : isRecorrente
                        ? <><FaSave /> Lançar {parcelas} Parcela{parcelas !== 1 ? 's' : ''}</>
                        : <><FaSave /> Salvar Conta</>
                  }
                </button>
              </div>

            </form>
          )}
        </div>
      </div>

      {/* [ANEXOS] Visualizador — fora do overlay para não disparar o onClose do modal */}
      {anexoParaVer && (
        <AnexoViewerModal
          anexoUrl={anexoParaVer}
          onClose={() => setAnexoParaVer(null)}
        />
      )}
    </>
  );
};

export default ModalContasAPagar;